import "./style.scss";

import { artworkNatureList } from "artworkNatureList";
import NewForm from "components/NewForm";
import { handleScrollToErrors } from "components/NewForm/utils";
import DeleteModal from "components/shared/DeleteModal";
import type { TFormCustomField } from "components/TitleFormContainer/types";
import { useFormatChannels } from "containers/Channels/utils";
import { formatCustomFields, formatFieldsForDefaultValues } from "containers/CustomFieldsContainer/helpers";
import type { IFormStep } from "containers/TermFormContainer";
import { formatNumber, generateRandomIssn } from "helpers/utils";
import { resolvePressChannelSlot } from "helpers/pressCalculationBase";
import { useCompanySettings } from "hooks/useCompanySettings";
import { usePermissions } from "hooks/usePermissions";
import React, { useEffect, useState } from "react";
import { useMemo } from "react";
import type { Control, FieldErrors, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { fetchCustomFields, ICustomField } from "store/customFieldsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { fetchCreateNewTitle, fetchDeleteTitle, fetchUpdateTitlePress } from "store/titleStore";
import type { TFetchStatus } from "store/types";
import { ChannelType } from "store/types";

import CalculationMethodStep from "./CalculationMethodStep";
import NewPublicationStep from "./NewPublicationStep";

export type TPublicationData = {
    avatarUrl: string;
    publicationName: string;
    issnCode: string;
    parutionCode: string;
    artworkNature: string | { code: string; description: string } | undefined;
    pricePerFolio: string;
    deliveryDate: string | Date | null;
    releaseDate: string | Date | null;
    analysisCode: string;
    customFields: TFormCustomField[];
};

export type TPublicationFormData = {
    control: Control<TPublicationData, any>;
    errors: FieldErrors<TPublicationData>;
    watch?: UseFormWatch<TPublicationData>;
    setValue?: UseFormSetValue<TPublicationData>;
};

interface IPublicationFormContainer {
    mode: "new" | "edit-page";
    /**
     * Is edit mode active
     */
    editModeActive?: boolean;
    /**
     * Closes edit mode.
     */
    closeEditMode?: () => void;
    handleModalClose?: (type: "publication" | "beneficiar", data: any) => Promise<void>;
}

/**
 * Form container for publications (used by press and audiovisual companies).
 */
const PublicationFormContainer = ({
    mode,
    editModeActive,
    closeEditMode,
    handleModalClose,
}: IPublicationFormContainer) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { hasPermissions } = usePermissions();

    const { channels } = useAppSelector((state) => state.channels);
    const { title: publication } = useAppSelector((state) => state.titles);
    const { customFields, customFieldsStatus } = useAppSelector((state) => state.customFields);
    const cleanedChannels = useFormatChannels(channels, "press");

    const [submitFormStatus, setSubmitFormStatus] = useState<TFetchStatus>("idle");
    const [modalType, setModalType] = useState("");

    const { getCompanySettings, companyInfos } = useCompanySettings();
    const publisherType = companyInfos?.publisherType;

    // Preferred identifier - ISSN or internal code:
    const preferredIdentifier = getCompanySettings("preferred_title_identifier");
    const isPreferredIdentifierConcat = preferredIdentifier === "concat";

    // Calculation methods:
    const usesBothCalculationMethods = getCompanySettings("press_uses_multiple_channels");
    const preferredChannelId = getCompanySettings("press_preferred_channel");
    const preferredChannel = cleanedChannels.find((channel) => channel.id === +preferredChannelId);
    const isFolioBased = resolvePressChannelSlot(preferredChannel ?? { name: "", type: null }) === "folio";

    const currentCustomFields = useMemo(() => {
        return customFields.filter((field) => field.relatedResource === "title");
    }, [customFields]);

    /**
     * Saved default publication price (needed on edit).
     */
    const publicationChannelPrice = useMemo(() => {
        if (publication && publication.overriddenChannelsData) {
            return publication?.overriddenChannelsData[0]?.price ?? 0;
        } else {
            return 0;
        }
    }, [publication]);

    const defaultValues: TPublicationData = useMemo(() => {
        const defaultArtworkNature = artworkNatureList.find(
            (artwork) => artwork.code == (publisherType === "press" ? "013" : "006"),
        );

        const issnCode = isPreferredIdentifierConcat ? publication?.otherInformation?.issn : publication?.ean13;

        const defaultValues: TPublicationData = {
            avatarUrl: editModeActive ? publication?.avatarUrl ?? "" : "",
            publicationName: editModeActive ? publication?.name ?? "" : "",
            issnCode: editModeActive ? issnCode ?? "" : "",
            parutionCode: editModeActive ? publication?.otherInformation?.parutionCode ?? "" : "",
            artworkNature: editModeActive
                ? artworkNatureList.find((artwork) => artwork.code == publication?.artworkNature) ?? ""
                : defaultArtworkNature,
            pricePerFolio: editModeActive
                ? formatNumber(publicationChannelPrice / 100, { always2Digit: true }) ?? ""
                : "",
            deliveryDate: editModeActive ? publication?.otherInformation?.deliveryDate ?? null : null,
            releaseDate: editModeActive ? publication?.releaseDate ?? null : null,
            analysisCode: editModeActive ? publication?.analysisCode ?? "" : "",
            customFields: [],
        };

        // If there are custom fields created, add them to the default values:
        if (currentCustomFields.length > 0) {
            const customFields = formatFieldsForDefaultValues({
                customFields: currentCustomFields,
                editModeActive,
                typeData: publication,
            });
            defaultValues.customFields = customFields ?? [];
        }

        return defaultValues;
    }, [publication]);

    const methods = useForm<TPublicationData>({ defaultValues, mode: "all" });

    const {
        control,
        trigger,
        handleSubmit,
        formState: { isDirty, dirtyFields, errors },
        watch,
        setValue,
    } = methods;

    const onSubmit = async (formData: TPublicationData) => {
        setSubmitFormStatus("pending");
        const folioChannel = cleanedChannels.find((channel) => channel.pressSlot === "folio");
        const data: any = {};
        data.name = formData.publicationName?.trim() ?? "";
        data.overriddenChannelsData = [
            {
                price:
                    isFolioBased || usesBothCalculationMethods
                        ? Math.round(+formData.pricePerFolio.replace(",", ".") * 100) ?? 0
                        : 100,
                channelId: folioChannel?.id ?? 0,
            },
        ];
        data.releaseDate = formData.releaseDate ?? null;
        data.otherInformation = {
            deliveryDate: formData.deliveryDate ?? null,
            parutionCode: formData.parutionCode ?? "",
        };
        data.customFields = formatCustomFields(formData.customFields, currentCustomFields);

        if (isPreferredIdentifierConcat) {
            data.otherInformation.issn = formData.issnCode == "" ? generateRandomIssn() : formData.issnCode;
            data.ean13 = `${data.otherInformation.issn}${data.otherInformation.parutionCode}`;
        } else {
            data.ean13 = formData.issnCode == "" ? generateRandomIssn() : formData.issnCode;
        }

        data.analysisCode = formData.analysisCode ?? "";
        data.artworkNature = typeof formData.artworkNature !== "string" && (formData.artworkNature?.code ?? "");
        data.avatarUrl = formData.avatarUrl ?? "";

        if (mode === "new") {
            const response = await dispatch(fetchCreateNewTitle({ data }));
            if (response.meta.requestStatus === "fulfilled") {
                if (handleModalClose)
                    handleModalClose("publication", {
                        name: data.name,
                        id: response.payload.result.result.createdTitleId,
                    });
                else navigate(`/publications/${response.payload.result.result.createdTitleId}`);
                setSubmitFormStatus("succeeded");
            } else {
                setSubmitFormStatus("failed");
            }
        } else {
            if (publication == undefined || publication == null) return;
            const response = await dispatch(fetchUpdateTitlePress({ data, id: publication.id }));
            if (response.meta.requestStatus === "fulfilled") {
                closeEditMode && closeEditMode();
                // dispatch(getAllArticles(publication.id));
                setSubmitFormStatus("succeeded");
            } else {
                setSubmitFormStatus("failed");
            }
        }
    };

    const handleDeletePublication = async () => {
        if (!publication) return;
        const response = await dispatch(fetchDeleteTitle(publication.id));
        if (response.meta.requestStatus === "fulfilled") {
            navigate("/publications");
            closeEditMode && closeEditMode();
        }
    };

    const publicationFormSteps: IFormStep[] = [
        {
            stepTitle:
                mode === "new"
                    ? t(`NewPublication.NewPublication_${publisherType}`)
                    : t(`Publications.EditPublication_${publisherType}`),
            stepSubtitle: publisherType === "press" ? t("NewPublication.NewPublicationSubtitle") : "",
            format: (stepTitle: string, stepSubtitle: string) => (
                <NewPublicationStep
                    stepTitle={stepTitle}
                    stepSubtitle={stepSubtitle}
                    mode={mode}
                    indexStep={0}
                    useFormData={{ control, errors, watch, setValue }}
                    isPreferredIdentifierConcat={isPreferredIdentifierConcat}
                    companyId={companyInfos?.id}
                    customFields={currentCustomFields.filter((field) => field.formLocation === "generalInfo")}
                />
            ),
        },
        {
            stepTitle: isFolioBased ? t("NewPublication.FolioAndDates") : t("NewPublication.Dates"),
            stepSubtitle: t("NewPublication.FolioAndDatesSubtitle"),
            format: (stepTitle: string, stepSubtitle: string) => (
                <CalculationMethodStep
                    stepTitle={stepTitle}
                    stepSubtitle={stepSubtitle}
                    mode={mode}
                    indexStep={1}
                    useFormData={{ control, errors }}
                    calculationMethod={{ isFolioBased, usesBothCalculationMethods }}
                    customFields={currentCustomFields.filter((field) => field.formLocation === "dates")}
                />
            ),
        },
    ];

    const deleteStep = {
        title: t(`Publications.DeletePublication_${publisherType}`),
        subtitle: t(`Publications.DeleteText_${publisherType}`),
    };

    const deleteConfirmationDescription = (
        <>
            <p>{t(`Publications.DeleteText_${publisherType}`)}</p>
        </>
    );

    const currentPublication = watch("publicationName");

    const dataForSteps = [`${currentPublication}`];

    useEffect(() => {
        if (["idle", "pending"].includes(customFieldsStatus)) {
            dispatch(fetchCustomFields()).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    const newCustomFields = formatFieldsForDefaultValues({
                        customFields: resp.payload.filter((item) => item.relatedResource === "title"),
                        editModeActive,
                        typeData: publication,
                    });
                    setValue("customFields", newCustomFields);
                }
            });
        }
    }, [customFieldsStatus]);

    return (
        <div className="publication-form-container">
            {/** DELETE TITLE MODAL */}
            {editModeActive && (
                <DeleteModal
                    open={modalType === "delete"}
                    onClose={() => setModalType("")}
                    onDelete={handleDeletePublication}
                    elementTitle={publication?.name ?? ""}
                    needsDoubleQuotes
                    needsConfirmation
                    confirmationText={t(`NewPublication.DeleteConfirmationText_${publisherType}`)}
                    description={deleteConfirmationDescription}
                />
            )}
            <FormProvider {...methods}>
                <NewForm
                    steps={publicationFormSteps}
                    mode={mode}
                    onSubmit={onSubmit}
                    editModeActive={editModeActive}
                    closeEditMode={closeEditMode}
                    useFormData={{ trigger, handleSubmit, isDirty, dirtyFields }}
                    onDelete={() => setModalType("delete")}
                    txtBtnSubmitNew={t(`NewPublication.CreateIssue_${publisherType}`)}
                    dataForSteps={dataForSteps}
                    submitFormStatus={submitFormStatus}
                    handleScrollToErrors={() => handleScrollToErrors(errors)}
                    deleteStep={hasPermissions(["publication:new"]) ? deleteStep : undefined}
                    infoLink={{
                        type: t(`Publications.Publication_${publisherType}`, { count: 1 }).toLowerCase(),
                        link: "https://presse-helpdesk.crealo.app/fr/article/creer-une-parution-1ngsuht/",
                        context: "female",
                    }}
                />
            </FormProvider>
        </div>
    );
};

export default React.memo(PublicationFormContainer);
